import { Check } from 'lucide-react'
import { cn } from '../lib/utils'
import { blockTheme } from '../block-theme/blockTheme'
import { quizOptionStyle, quizStateColor, type QuizOptionState } from '../block-theme/quizPalette'

export type { QuizOptionState }

interface Props {
  text: string
  /** Подсказка автору, пока текст варианта пуст: серым, как плейсхолдер поля. */
  placeholder?: string
  state?: QuizOptionState
  multi?: boolean
  disabled?: boolean
  onClick?: () => void
}

/**
 * QuizOptionCard — одна карточка-ответ choice-квиза: индикатор выбора (круг у
 * одиночного, квадрат у множественного) + текст. Порт плеерного `QuizOption`
 * (academy-frontend `.../blocks/QuizOption/`): скругление 16px, рамка 1px,
 * цвет и тень состояния — из общей палитры `quizOptionStyle`.
 */
export const QuizOptionCard = ({
  text,
  placeholder,
  state = 'idle',
  multi = false,
  disabled = false,
  onClick,
}: Props) => {
  const fill = quizStateColor(state)
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex min-h-[54px] w-full cursor-pointer items-center gap-2.5 rounded-2xl border border-solid px-[13px] py-2 text-left text-[16px] font-semibold leading-[1.25] transition-[border-color,background-color,box-shadow,color,transform] duration-200 active:scale-[0.985] disabled:cursor-default',
        state === 'incorrect' ? 'animate-oa-shake' : null,
      )}
      style={quizOptionStyle(state)}
    >
      <span
        className={cn(
          'pointer-events-none flex size-[23px] shrink-0 items-center justify-center border border-solid transition-all',
          multi ? 'rounded-md' : 'rounded-full',
        )}
        style={
          fill
            ? { backgroundColor: fill, borderColor: fill }
            : { borderColor: blockTheme.quiz.idleBorder }
        }
      >
        {fill ? (
          <Check
            className="size-3.5"
            strokeWidth={3}
            style={{ color: blockTheme.quiz.primaryFg }}
          />
        ) : null}
      </span>
      <span
        data-option-text
        className="pointer-events-none min-w-0 flex-1 [overflow-wrap:anywhere]"
        style={text || !placeholder ? undefined : { color: blockTheme.quiz.mutedText }}
      >
        {text || placeholder}
      </span>
    </button>
  )
}
